import { forwardRef } from 'react'

import { ButtonTypes, LinkTypes } from '@cox/core-ui8/dist/Button'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'

import ReactImageCarouselItemSmallSkeleton from './ReactImageCarouselItemSmallSkeleton'
import { DynamicMediaComponents, getDynamicMediaFromSrc } from '../../../../utils/dynamic-media-util'
import { getImageSrc } from '../../../../utils/local-util'
import { getAttributeValue, getConditionResult, hasCFConditionals } from '../../../../utils/rule-builder-util'
import { RuleType } from '../../../../utils/types'
import ReactButton from '../../../ReactButton'
import ReactImage from '../../../ReactImage'
import ReactList, { ListType } from '../../../ReactList'
import ReactText from '../../../ReactText'
import { ContentFragmentElements } from '../../types'

const ReactImageCarouselItemSmall = forwardRef((props: ContentFragmentElements, ref: any): JSX.Element => {
  const {
    index = 0,
    minPlacardHeight,
    image,
    imageParams,
    altImage,
    heading,
    description,
    bulletIcon,
    bulletIconAltText,
    bulletPoints,
    buttonTextPrimary,
    buttonLinkPrimary,
    buttonLinkNewTabPrimary,

    ruleExpression = '',
    ruleType,
    condition,
    tokenProperties,
    linkTypeButtonPrimary = { value: '' },
    linkIdButtonPrimary = { value: '' },
    contentFragmentId = { value: '' },
    id = ''
  } = props

  const flexOffers = useFlexOffers(ruleExpression)
  // get value if it is dynamic, or else return value authored value
  const getValue = (attribute: any, propertyName?: string) => {
    if (hasCFConditionals(attribute)) {
      const jsonAttribute = (props as any)?.[`${propertyName}_json`]
      return getAttributeValue(jsonAttribute, flexOffers, index, tokenProperties, propertyName)
    }
    return getAttributeValue(attribute, flexOffers, index, tokenProperties, propertyName)
  }
  if (flexOffers?.loading) {
    return <ReactImageCarouselItemSmallSkeleton />
  }
  // flex offers success
  let conditionResult = true
  if (flexOffers?.success && ruleType === RuleType.conditional && condition) {
    conditionResult = getConditionResult(condition, flexOffers)
  }
  if (conditionResult) {
    const contentFragmentIdValue = getValue(contentFragmentId, 'contentFragmentId')
    const idValue = getValue(id, 'id')
    const slideId = contentFragmentIdValue || idValue
    const altImageValue = getValue(altImage, 'altImage')
    const imageParamsValue = getValue(imageParams, 'imageParams')
    const imageValue = getDynamicMediaFromSrc(
      getValue(image, 'image') as string,
      (imageParamsValue as DynamicMediaComponents) || DynamicMediaComponents.CAROUSEL_IMAGERY
    )
    const headingValue = getValue(heading, 'heading')
    const descriptionValue = getValue(description, 'description')
    const buttonTextPrimaryValue = getValue(buttonTextPrimary, 'buttonTextPrimary')
    const buttonLinkPrimaryValue = getValue(buttonLinkPrimary, 'buttonLinkPrimary')
    const buttonLinkNewTabPrimaryValue = getValue(buttonLinkNewTabPrimary, 'buttonLinkNewTabPrimary')
    const linkTypeButtonPrimaryValue = linkTypeButtonPrimary?.value
    const linkIdButtonPrimaryValue = getValue(linkIdButtonPrimary, 'linkIdButtonPrimary')
    const bulletList = {
      icon: getImageSrc(getValue(bulletIcon, 'bulletIcon')),
      altIcon: getValue(bulletIconAltText, 'bulletIconAltText'),
      items: bulletPoints?.value?.map((bullet) => getValue(bullet))
    }
    return (
      <div className='image-carousel-small-item' id={slideId}>
        {/* Image of Slide */}
        <div key='image-container' className='image-container'>
          {imageValue ? (
            <div key='image' className='image'>
              <ReactImage alt={altImageValue} src={imageValue} />
            </div>
          ) : (
            <div key='image-placeholder' className='image-placeholder' />
          )}

          <div className='container placard'>
            <div
              ref={(refItem: any) => ((ref.current[index] as any) = refItem)}
              className='carousel-item-under-image'
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: minPlacardHeight
              }}
            >
              <div>
                {/* Heading of Slide */}
                <div key='heading' className='heading'>
                  {/* TODO Anchor tag is creating issue.  CCCOREUI-3410*/}
                  {/* <a href='#' tabIndex={0} onClick={(e) => e.preventDefault()}> */}
                  <ReactText text={headingValue} isParsed />
                  {/* </a> */}
                </div>

                {/* Heading Description of Slide */}
                <div key='headingDescription' className='heading-description'>
                  <ReactText text={descriptionValue} isParsed />
                </div>

                {/* List under slide */}
                <ReactList
                  listFrom={ListType.cfStandardList}
                  icon={bulletList.icon}
                  altIcon={bulletList.altIcon}
                  cfListItems={bulletList.items}
                />
              </div>

              {/* CTA */}
              {buttonTextPrimaryValue && buttonTextPrimaryValue.length > 0 && (
                <div className='cta-section-container small-device'>
                  <ReactButton
                    text={buttonTextPrimaryValue}
                    linkUrl={buttonLinkPrimaryValue}
                    openInNewTab={buttonLinkNewTabPrimaryValue ? true : false}
                    buttonTypes={ButtonTypes.PRIMARY}
                    linkType={linkTypeButtonPrimaryValue as LinkTypes}
                    linkId={linkIdButtonPrimaryValue}
                    index={index}
                    ruleExpression={ruleExpression}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
  return <></>
})

export default ReactImageCarouselItemSmall
